import axios from "axios";

import { useAuthStore } from "../store/authStore";
import WebApp from "@twa-dev/sdk";
import { env } from "@config/env";
import { TelegramAuthData } from "@telegram-auth/react";
import type { AuthResponse } from "../types/auth";
import { logger } from "@/shared/utils/logger";
import { restoreSession } from "../utils/restoreSession";
import { useUserStore } from "../store/user";

export const authRefreshApi = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true, // Ensure cookies are sent for refresh token
});

export const apiWithAuth = axios.create({
  baseURL: env.apiBaseUrl,
});

export const apiNoAuth = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true,
});

// Function to refresh access token
export const refreshAccessToken = async () => {
  try {
    const response = await authRefreshApi.post("/auth/refresh");
    return response.data.accessToken as string;
  } catch (error) {
    logger.error("Failed to refresh access token:", error);
    throw error;
  }
};

// Mini App
export const loginWithTelegramInitData = async (
  initData: string
): Promise<AuthResponse> => {
  logger.debug("Sending initData for Telegram auth:", initData);
  const response = await authRefreshApi.post<AuthResponse>("/auth/telegram", {
    initData,
  });
  logger.debug("Received auth response from initData:", response.data);
  return response.data;
};

// Web
export const loginWithTelegramAuthButton = async (
  authBtnData: TelegramAuthData
): Promise<AuthResponse> => {
  logger.debug("Sending authBtnData for Telegram auth:", authBtnData);
  const response = await authRefreshApi.post<AuthResponse>("/auth/telegram", {
    authBtnData,
  });
  logger.debug("Received auth response from authBtnData:", response.data);
  return response.data;
};

// Logout function (only for web)
export const logout = async () => {
  if (!WebApp.initData) {
    await authRefreshApi.post("/auth/logout");
    useAuthStore.getState().logout();
    useUserStore.getState().stopPolling();
    logger.info("User logged out successfully.");
  } else {
    logger.warn("Logout is disabled inside Mini App.");
  }
};

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

apiWithAuth.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiWithAuth.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      logger.warn("Received 401, trying to refresh token...");

      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiWithAuth(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        useAuthStore.getState().setAccessToken(newToken);
        refreshSubscribers.forEach((callback) => callback(newToken));
        refreshSubscribers = [];
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiWithAuth(originalRequest);
      } catch (refreshError: unknown) {
        const restored = await restoreSession();
        if (restored) {
          const token = useAuthStore.getState().accessToken;
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiWithAuth(originalRequest);
          }
        }

        useAuthStore.getState().logout();
        useUserStore.getState().stopPolling();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
